// Typed wrappers for go-pim document templates.
//
// TWO ROUTES, AND THE DIFFERENCE MATTERS:
//
//   GET /v1/templates                       the six compiled builtins, global
//   GET /v1/companies/:id/templates         builtins AND that company's own
//
// The second is the one a picker must use. Since migration 0013 an operator can
// author templates (#62 phase 2), and a picker fed from the global route would
// not list the template they just created — the single most confusing possible
// outcome of authoring one.
//
// THE FOUR THINGS A READER GETS WRONG HERE:
//
//   1. EDITING A TEMPLATE NEVER CHANGES A DOCUMENT ALREADY ISSUED. Every
//      document freezes its own copy of the definition it was minted with. Say
//      so in the UI: an operator who believes an edit repairs a receipt already
//      sent will tell a customer something untrue.
//   2. `taxable` is a TAX BACKSTOP, not a preference. false forces computed tax
//      to ZERO whatever the company's taxation scheme says. The dangerous
//      direction is false-on-an-invoice — see taxabilityWarning below.
//   3. Builtins are READ-ONLY and shared by every company. The company route
//      tags them `built_in` so the panel never merges two sources or guesses at
//      precedence.
//   4. Retiring (`active: false`) is not deleting. A retired template stops
//      resolving, so a settle falls back to a GENERIC — a silent downgrade for
//      any company still pointing at that key.
//
// Scope is by company id in the path on the collection route; the item routes
// are flat (/v1/templates/:id) and carry no company segment.
import { apiClient } from "./client";

/** One declared field a template renders. */
export interface TemplateField {
  key: string;
  label?: string;
  type?: string;
  required?: boolean;
}

/**
 * Includes payment_confirmation, which go-pim has been able to issue since #59.
 * The two legacy pickers filter to receipt/invoice by doc_type anyway, so
 * widening this cannot put a confirmation in the wrong dropdown.
 */
export type TemplateDocType = "receipt" | "invoice" | "payment_confirmation";

export const TEMPLATE_DOC_TYPES: TemplateDocType[] = [
  "receipt",
  "invoice",
  "payment_confirmation",
];

export const TEMPLATE_DOC_TYPE_LABELS: Record<TemplateDocType, string> = {
  receipt: "Receipt",
  invoice: "Invoice",
  payment_confirmation: "Payment confirmation",
};

export interface DocTemplate {
  key: string;
  doc_type: TemplateDocType;
  title: string;
  taxable: boolean;
  fields: TemplateField[];
}

/** A company-scoped template: a builtin, or one the operator authored. */
export interface DocumentTemplate extends DocTemplate {
  /** Empty for a builtin — they have no row and cannot be edited. */
  id: string;
  active: boolean;
  built_in: boolean;
}

/** listTemplates — GET /v1/templates. The global builtin registry. */
export async function listTemplates(): Promise<DocTemplate[]> {
  const resp = await apiClient.get<{ templates: DocTemplate[] }>(
    "/v1/templates",
  );
  return resp.data.templates ?? [];
}

/**
 * listCompanyTemplates — GET /v1/companies/:id/templates.
 *
 * Builtins AND the company's own, each tagged. Use this for anything an
 * operator picks from.
 */
export async function listCompanyTemplates(
  companyId: string,
): Promise<DocumentTemplate[]> {
  const resp = await apiClient.get<{ templates: DocumentTemplate[] }>(
    `/v1/companies/${companyId}/templates`,
  );
  return resp.data.templates ?? [];
}

export type TemplateWrite = Omit<DocumentTemplate, "id" | "built_in">;

export async function createTemplate(
  companyId: string,
  t: TemplateWrite,
): Promise<DocumentTemplate> {
  const resp = await apiClient.post<DocumentTemplate>(
    `/v1/companies/${companyId}/templates`,
    t,
  );
  return resp.data;
}

export async function updateTemplate(
  id: string,
  t: TemplateWrite,
): Promise<DocumentTemplate> {
  const resp = await apiClient.put<DocumentTemplate>(`/v1/templates/${id}`, t);
  return resp.data;
}

export async function deleteTemplate(id: string): Promise<void> {
  await apiClient.delete(`/v1/templates/${id}`);
}

// ─── Validation, mirroring go-pim's normalizeTemplate ───────────────────────
//
// Mirrored rather than approximated. A panel that accepts MORE than the server
// turns a typo into a 400 after a round trip; one that accepts LESS silently
// forbids values that are fine, and the operator has no way to learn why. Both
// grammars are deliberately narrow identifier shapes — see the comment on
// templateKeyRe in go-pim's internal/storage/templates.go.

export const TEMPLATE_KEY_RE = /^[a-z][a-z0-9_]{2,49}$/;
export const FIELD_KEY_RE = /^[a-z][a-z0-9_]{0,39}$/;
export const MAX_TEMPLATE_TITLE_LEN = 120;
export const MAX_TEMPLATE_FIELDS = 20;
export const MAX_FIELD_LABEL_LEN = 80;

/**
 * The six compiled builtins. A custom key may not shadow one — go-pim refuses
 * it rather than resolving by precedence, because whichever way a precedence
 * rule went, an operator would get a document they did not design with nothing
 * saying which definition won.
 *
 * Duplicated here only to explain the refusal BEFORE the round trip. The server
 * is the authority; this list going stale costs a late error message, never a
 * wrong document.
 */
export const BUILTIN_TEMPLATE_KEYS = [
  "donation_80g",
  "solar_invoice",
  "gift_card",
  "generic_receipt",
  "generic_invoice",
  "generic_payment_confirmation",
];

/** Returns why this key is unusable, or null when it is fine. */
export function templateKeyError(key: string): string | null {
  const k = key.trim().toLowerCase();
  if (k === "") return "A key is required.";
  if (!TEMPLATE_KEY_RE.test(k))
    return "3–50 characters: lowercase letters, digits and underscores, starting with a letter.";
  if (BUILTIN_TEMPLATE_KEYS.includes(k))
    return `“${k}” is a built-in template — choose a different key.`;
  return null;
}

/** Returns why this field key is unusable, or null when it is fine. */
export function fieldKeyError(key: string): string | null {
  const k = key.trim().toLowerCase();
  if (k === "") return "A field key is required.";
  if (!FIELD_KEY_RE.test(k))
    return "Lowercase letters, digits and underscores, starting with a letter.";
  return null;
}

/**
 * The warning that matters, and the only one here about money.
 *
 * `taxable: false` forces computed tax to zero regardless of the company's
 * taxation scheme. On a donation receipt that is correct and is the point. On a
 * real tax invoice for a company that HAS a tax scheme, it under-states output
 * tax on a document frozen at issue and impossible to recompute — silent until
 * an audit, unlike over-charging, which a customer reports the same day.
 *
 * Deliberately one-directional: taxable-on-a-donation is the loud failure and
 * needs no warning, because the person harmed finds out immediately.
 *
 * Returns null when there is nothing to say.
 */
export function taxabilityWarning(
  t: Pick<DocumentTemplate, "doc_type" | "taxable">,
  companyTaxation: string,
): string | null {
  if (t.doc_type !== "invoice") return null;
  if (t.taxable) return null;
  if (companyTaxation === "none" || companyTaxation === "") return null;
  return "This invoice template computes NO tax, but the company has a tax scheme configured. Every invoice issued under it will under-state output tax — and an issued document is frozen, so it cannot be recomputed later. Tick “computes tax” unless you are certain this supply is exempt.";
}
